import { routerAuthNotifyPlugin } from '@0x234/common-vue/plugins/router-auth-notify-plugin';
import { strategyProperties } from 'configs/strategies';
import { route } from 'quasar/wrappers';
import AppRoute from 'src/router/app-route';
import { useAppStore } from 'stores/app-store';
import { useAuthStore } from 'stores/auth-store';
import { useSiteContextStore } from 'stores/site-context-store';
import { createMemoryHistory, createRouter, createWebHashHistory, createWebHistory } from 'vue-router';
import routes, { employerRoutes, nonContextualRoutes, Route, SiteContext } from './routes';

export default route(() => {
    const router = createRouter({
        routes,
        history: configureHistoryMode(process.env.VUE_ROUTER_BASE),
        scrollBehavior(to, from, savedPosition) {
            if (savedPosition) {
                // If there is a saved position, return it (this happens when using browser back/forward buttons).
                return savedPosition;
            } else if (to.meta?.retainScrollPosition) {
                return { left: window.scrollX, top: window.scrollY, behavior: 'smooth' };
            } else {
                return { left: 0, top: 0, behavior: 'instant' };
            }
        }
    });
    configureAuthMiddleware(router);
    configureSiteContextMiddleware(router);
    configureErrorHandler(router);
    routerAuthNotifyPlugin.install(router, {
        isUserAuthenticated: () => useAuthStore().isUserAuthenticated,
        strategies: Object.entries(strategyProperties)
            .filter(([, strategy]) => strategy.domain)
            .reduce((strategies, [strategyId, strategy]) => {
                strategies[strategyId] = {
                    name: strategy.name,
                    domain: strategy.domain
                };
                return strategies;
            }, {})
    });
    return router;
});

function configureHistoryMode(routerBase) {
    const historyMode = process.env.SERVER
        ? createMemoryHistory
        : (process.env.VUE_ROUTER_MODE === 'history' ? createWebHistory : createWebHashHistory);
    return historyMode(routerBase);
}

function configureAuthMiddleware(router) {
    router.beforeEach(async (to, from, next) => {
        const authStore = useAuthStore();
        await useAuthStore().loadUser();
        if (to.meta?.public !== true && !authStore.isUserAuthenticated) {
            return next(AppRoute.build(Route.LoginPage));
        }
        if (authStore.user && !authStore.user?.isReady && to.name !== Route.AccountSetupPage && !to.meta?.bypassSetup) {
            return next(AppRoute.build(Route.AccountSetupPage));
        }
        if (authStore.user && authStore.user.isReady && to.name === Route.AccountSetupPage) {
            return next(AppRoute.build(Route.ManageUserProfile));
        }
        return next();
    });
}

function configureSiteContextMiddleware(router) {
    router.beforeEach(async (to, from, next) => {
        const siteContextStore = useSiteContextStore();
        if (nonContextualRoutes.includes(to.name)) {
            next();
            return;
        }
        let isEmployerSite = false;
        if (employerRoutes.includes(to.name)) {
            isEmployerSite = true;
        } else if (to.matched?.some(record => record.name && employerRoutes.includes(record.name))) {
            isEmployerSite = true;
        } else if (to.meta?.employerSite) {
            isEmployerSite = true;
        }
        siteContextStore.setSiteContext(
            isEmployerSite
                ? SiteContext.Employer
                : SiteContext.JobSeeker
        );
        next();
    });
}

function configureErrorHandler(router) {
    router.onError(async err => {
        if (err.message.includes('error loading dynamically imported module')
            || err.message.includes('404')
            || (err.response && err.response.status === 404)) {
            try {
                const response = await fetch('/status');
                if (response.ok) {
                    useAppStore().newVersionServiceDialog = {};
                    console.warn('A new version of the application is available. Please refresh your browser.');
                    return;
                }
                useAppStore().unavailableServiceDialog = { type: 'client' };
                console.error('Server is responding but not OK:', response.statusText);
            } catch (_err) {
                useAppStore().unavailableServiceDialog = { type: 'client' };
                console.error('Server seems to be unavailable:', _err);
            }
        }
    });
}
